import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const PaymentSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [existingMethod, setExistingMethod] = useState(null);
  const [formData, setFormData] = useState({
    iban: '',
    accountHolderName: '',
    bankName: '',
    paymentPreference: 'bank_transfer',
    isDefault: true
  });
  const [formErrors, setFormErrors] = useState({});

  const paymentPreferenceOptions = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'card_payment', label: 'Card Payment' },
    { value: 'digital_wallet', label: 'Digital Wallet' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'stripe', label: 'Stripe' }
  ];

  useEffect(() => {
    if (user) {
      fetchExistingPaymentMethod();
    }
  }, [user]);

  const fetchExistingPaymentMethod = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        ?.from('payment_methods')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('is_default', true)
        ?.maybeSingle();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        console.error('Error fetching payment method:', fetchError);
      } else if (data) {
        setExistingMethod(data);
        setFormData({
          iban: data?.iban || '',
          accountHolderName: data?.account_holder_name || '',
          bankName: data?.bank_name || '',
          paymentPreference: data?.payment_preference || 'bank_transfer',
          isDefault: data?.is_default || true
        });
      }
    } catch (err) {
      console.error('Failed to fetch payment method:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateIBAN = (iban) => {
    const cleanIBAN = iban?.replace(/\s/g, '')?.toUpperCase();
    if (cleanIBAN?.length < 15 || cleanIBAN?.length > 34) {
      return 'IBAN must be between 15 and 34 characters';
    }
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/?.test(cleanIBAN)) {
      return 'Invalid IBAN format (e.g., GB29NWBK60161331926819)';
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData?.iban?.trim()) {
      errors.iban = 'IBAN is required';
    } else {
      const ibanError = validateIBAN(formData?.iban);
      if (ibanError) {
        errors.iban = ibanError;
      }
    }

    if (!formData?.accountHolderName?.trim()) {
      errors.accountHolderName = 'Account holder name is required';
    }

    if (!formData?.paymentPreference) {
      errors.paymentPreference = 'Payment preference is required';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (formErrors?.[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const cleanIBAN = formData?.iban?.replace(/\s/g, '')?.toUpperCase();

      const paymentMethodData = {
        user_id: user?.id,
        iban: cleanIBAN,
        account_holder_name: formData?.accountHolderName,
        bank_name: formData?.bankName || null,
        payment_preference: formData?.paymentPreference,
        is_default: formData?.isDefault,
        verification_status: 'pending'
      };

      let result;
      if (existingMethod) {
        // Update existing payment method
        result = await supabase
          ?.from('payment_methods')
          ?.update(paymentMethodData)
          ?.eq('id', existingMethod?.id)
          ?.select()
          ?.single();
      } else {
        // Insert new payment method
        result = await supabase
          ?.from('payment_methods')
          ?.insert([paymentMethodData])
          ?.select()
          ?.single();
      }

      if (result?.error) {
        throw result?.error;
      }

      // Navigate back to checklist
      navigate('/profile-setup-checklist');
    } catch (err) {
      console.error('Failed to save payment method:', err);
      setError(err?.message || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  const getVerificationStatusBadge = () => {
    if (!existingMethod) return null;

    const statusConfig = {
      pending: {
        label: 'Verification Pending',
        icon: 'Clock',
        bgColor: 'bg-warning/10',
        textColor: 'text-warning',
        iconColor: 'var(--color-warning)'
      },
      verified: {
        label: 'Verified',
        icon: 'CheckCircle2',
        bgColor: 'bg-success/10',
        textColor: 'text-success',
        iconColor: 'var(--color-success)'
      },
      failed: {
        label: 'Verification Failed',
        icon: 'XCircle',
        bgColor: 'bg-error/10',
        textColor: 'text-error',
        iconColor: 'var(--color-error)'
      }
    };

    const status = statusConfig?.[existingMethod?.verification_status] || statusConfig?.pending;

    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${status?.bgColor}`}>
        <Icon name={status?.icon} size={16} color={status?.iconColor} />
        <span className={`text-sm font-medium ${status?.textColor}`}>
          {status?.label}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading payment information...</p>
            </div>
          </div>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate('/profile-setup-checklist')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm">Back to Checklist</span>
          </button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Payment Setup
              </h1>
              <p className="text-muted-foreground">
                Add your bank account details and payment preferences
              </p>
            </div>
            {existingMethod && (
              <div className="hidden md:block">
                {getVerificationStatusBadge()}
              </div>
            )}
          </div>

          {/* Mobile verification badge */}
          {existingMethod && (
            <div className="md:hidden mt-4">
              {getVerificationStatusBadge()}
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6 flex gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="font-medium mb-1">Secure Payment Information</p>
            <p className="text-muted-foreground">
              Your payment details are encrypted and stored securely. Verification typically takes 1-2 business days.
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-2xl">
          <div className="bg-card border border-border rounded-lg p-6 space-y-6">
            {/* IBAN Input */}
            <div>
              <Input
                label="IBAN (International Bank Account Number)"
                type="text"
                placeholder="GB29 NWBK 6016 1331 9268 19"
                value={formData?.iban}
                onChange={(e) => handleInputChange('iban', e?.target?.value)}
                error={formErrors?.iban}
                required
                description="Enter your IBAN without spaces (15-34 characters)"
              />
            </div>

            {/* Account Holder Name */}
            <div>
              <Input
                label="Account Holder Name"
                type="text"
                placeholder="John Doe"
                value={formData?.accountHolderName}
                onChange={(e) => handleInputChange('accountHolderName', e?.target?.value)}
                error={formErrors?.accountHolderName}
                required
                description="Full name as it appears on your bank account"
              />
            </div>

            {/* Bank Name */}
            <div>
              <Input
                label="Bank Name"
                type="text"
                placeholder="Example Bank"
                value={formData?.bankName}
                onChange={(e) => handleInputChange('bankName', e?.target?.value)}
                description="Optional: Name of your bank"
              />
            </div>

            {/* Payment Preference */}
            <div>
              <Select
                label="Payment Preference"
                options={paymentPreferenceOptions}
                value={formData?.paymentPreference}
                onChange={(value) => handleInputChange('paymentPreference', value)}
                error={formErrors?.paymentPreference}
                required
                description="Select your preferred payment method"
              />
            </div>

            {/* Verification Info */}
            {existingMethod?.verification_status === 'failed' && (
              <div className="bg-error/5 border border-error/20 rounded-lg p-4 flex gap-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-error mb-1">Verification Failed</p>
                  <p className="text-muted-foreground">
                    Please check your account details and try again. Contact support if the issue persists.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-error/5 border border-error/20 rounded-lg p-4 flex gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/profile-setup-checklist')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              iconName="Save"
              iconPosition="right"
            >
              {existingMethod ? 'Update Payment Method' : 'Save Payment Method'}
            </Button>
          </div>
        </form>
      </main>

      <MobileNav />
    </div>
  );
};

export default PaymentSetup;